import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { HERO_VIDEO, HERO_VIDEO_MOVIL, HERO_POSTER } from './media';
import { useSectionMotion, useMagnetico, EASE } from './motion';
import Frase from './Frase';
import { useIdioma } from '../../i18n/idioma';

interface HeroProps {
    onPrimary: () => void;
    signedIn: boolean;
}

/**
 * Portada: una imagen a sangre, una frase, una acción.
 *
 * Es la gramática que el equipo aprobó tras dos versiones descartadas. Lo que
 * se probó y NO funcionó, para que nadie lo repita: (1) el molde de siempre
 * —rótulo, titular, párrafo, dos botones y renglón de promesas— superpuesto a
 * un vídeo ahogado bajo tres capas de navy; (2) un hero partido en dos paneles
 * con canto duro. Lo que sí funciona es esto: el degradado va ANCLADO ABAJO,
 * opaco donde vive el texto y casi limpio arriba, así la imagen se ve de verdad.
 */
const Hero: React.FC<HeroProps> = ({ onPrimary, signedIn }) => {
    const { t } = useIdioma();
    const videoRef = useRef<HTMLVideoElement>(null);

    /* Qué variante se pide, decidido ANTES del primer pintado.
       Si se decidiera en un efecto, el navegador ya habría empezado a bajar la
       de escritorio y cambiar el `<source>` después obliga a un `load()` y a
       tirar lo descargado. */
    const [fuente] = useState(() =>
        typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches
            ? HERO_VIDEO_MOVIL
            : HERO_VIDEO,
    );

    /* ── Autoarranque INCONDICIONAL ────────────────────────────────────────
     *
     * Historia, porque esto ya cambió dos veces y conviene no volver a girarlo
     * sin saber lo que se gira:
     *
     *  1. Hubo un botón de pausa abajo a la derecha. El equipo lo quitó:
     *     ensuciaba la esquina de la portada.
     *  2. Al quitarlo, el freno se movió a `prefers-reduced-motion`, que era lo
     *     único que quedaba dando cumplimiento a la WCAG 2.2.2 (algo que se
     *     mueve solo más de cinco segundos tiene que poder detenerse).
     *  3. Y aquí estamos: con esa preferencia puesta, la portada se quedaba en
     *     el póster y el vídeo NO arrancaba nunca. Se pidió expresamente que
     *     reprodujera y repitiera siempre, así que el freno se retira.
     *
     * Lo que eso significa, dicho claro: hoy la portada NO tiene ninguna forma
     * de detenerse. Quien lleva la preferencia puesta porque el movimiento le
     * marea o le da migraña se come el vídeo igual. Si algún día quiere
     * cubrirse, el arreglo no es volver a apagarlo entero: es un botón de
     * pausa —basta con que aparezca al enfocar o al pasar por encima, para que
     * no ensucie la esquina—, y entonces esto se queda como está.
     *
     * `muted` se fuerza por propiedad antes de llamar a `play()`. React lo pone
     * como propiedad y no como atributo, y Chrome decide si deja arrancar
     * MIRANDO EL ATRIBUTO: sin esta línea, el autoarranque depende de que el
     * orden del commit salga a favor. Con ella, no depende de la suerte.
     */
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        video.muted = true;
        // Si aun así el navegador lo bloquea (pestaña en segundo plano, ahorro
        // de batería) la promesa se rechaza y queda el póster, que es un
        // fotograma del propio vídeo.
        void video.play().catch(() => undefined);
    }, []);

    const boton = useMagnetico<HTMLButtonElement>();

    const scope = useSectionMotion<HTMLElement>(({ gsap, scope, motion, full }) => {
        /* Entrada de la portada. Corre en los dos niveles de movimiento: con
           movimiento pleno las palabras suben desde detrás del renglón; con
           reducido aparecen fundidas, en el sitio. Nunca "nada". */
        const tl = gsap.timeline({ defaults: { ease: EASE.out } });

        tl.from(scope.querySelectorAll('[data-frase="titular"]'), {
            yPercent: full ? 112 : 0,
            autoAlpha: full ? 1 : 0,
            duration: full ? 1.4 : 0.8,
            stagger: full ? 0.085 : 0.05,
        }).from(
            scope.querySelectorAll('[data-entra]'),
            {
                y: full ? 20 : 0,
                autoAlpha: 0,
                duration: full ? 1.1 : 0.7,
                stagger: 0.14,
                ease: EASE.out,
            },
            full ? 0.7 : 0.3,
        );

        motion(() => {
            /* Acercamiento lentísimo de entrada: 3,4 s para un 12 %. No se
               "ve" pasar, pero es la diferencia entre una imagen pegada al
               fondo y una imagen que respira. */
            gsap.from(scope.querySelector('[data-hero-video]'), {
                scale: 1.12,
                duration: 3.4,
                ease: EASE.asiento,
            });

            /* ── La portada se hunde, no se va ──────────────────────────────
               La sección está pegada (`sticky`) dentro de un contenedor de su
               propia altura, así que no se desplaza: se queda quieta mientras
               el fold siguiente sube y la tapa. Para que eso se lea como
               PROFUNDIDAD y no como un fallo de pintado, la portada retrocede
               mientras la cubren — se encoge un punto, se oscurece y el vídeo
               sigue derivando. Es el gesto de «capas apiladas» que hace que un
               scroll se sienta con cuerpo. */
            gsap.timeline({
                scrollTrigger: {
                    /* El disparador es el fold SIGUIENTE, no la portada: la
                       portada está pegada y su posición ya no avanza, así que
                       medir sobre ella daría un progreso siempre a cero. El
                       recorrido va de «el siguiente fold asoma por abajo» a
                       «el siguiente fold ya llenó la pantalla», que es
                       exactamente el tramo en el que la portada queda tapada. */
                    trigger: scope.nextElementSibling,
                    start: 'top bottom',
                    end: 'top top',
                    // Un segundo de retraso: el fondo persigue al scroll en
                    // vez de ir clavado a la rueda.
                    scrub: 1,
                },
            })
                .to(scope.querySelector('[data-hero-video]'), { yPercent: 12, ease: EASE.none }, 0)
                .to(scope, { scale: 0.96, ease: EASE.none }, 0)
                .to(
                    scope.querySelector('[data-hero-copia]'),
                    { y: -70, autoAlpha: 0.2, ease: EASE.none },
                    0,
                )
                .to(scope.querySelector('[data-hero-sombra]'), { opacity: 0.55, ease: EASE.none }, 0);
        });
        /* El botón no lleva entrada propia: ya sube con la línea de tiempo de
           arriba, por su `data-entra`. Aquí hubo un `reveal('[data-hero-boton]')`
           que no animaba nada —ese atributo no existe en el marcado— y que se
           quedó mudo porque `reveal` descarta las listas vacías sin quejarse. */
    });

    /* ── El vídeo se detiene cuando ya nadie lo ve ──────────────────────────
       La portada está pegada (`sticky`): no se va de la pantalla, se queda
       debajo mientras el resto de la página la tapa. Para el navegador sigue
       estando visible, así que sigue decodificando 24 cuadros por segundo
       durante TODA la página, enterrada bajo contenido opaco.

       Medido en un teléfono con la CPU frenada cuatro veces: dos tercios del
       hilo principal durante el scroll se iban en pintado y decodificación, no
       en JavaScript. Y en un teléfono de verdad eso es batería que se va en
       cuadros que nadie llega a ver.

       Un IntersectionObserver no resuelve esto — precisamente porque `sticky`
       hace que el elemento nunca deje de intersecar. Lo que se mira es cuánto
       se ha bajado: pasada la altura de la portada, está tapada.

       Se añade también `visibilitychange`, para la pestaña en segundo plano. */
    useEffect(() => {
        const video = videoRef.current;
        const seccion = scope.current;
        if (!video || !seccion) return;

        let tapado: boolean | null = null;

        const revisar = () => {
            const ahora =
                document.hidden || window.scrollY > seccion.offsetHeight * 0.92;
            if (ahora === tapado) return;   // solo se actúa en el cambio
            tapado = ahora;
            if (ahora) {
                video.pause();
            } else {
                // Volver arriba reanuda siempre. Antes había aquí una condición
                // por `prefers-reduced-motion`; se retiró con el autoarranque.
                void video.play().catch(() => undefined);
            }
        };

        revisar();
        window.addEventListener('scroll', revisar, { passive: true });
        document.addEventListener('visibilitychange', revisar);
        return () => {
            window.removeEventListener('scroll', revisar);
            document.removeEventListener('visibilitychange', revisar);
        };
    }, [scope]);

    return (
        <section
            ref={scope}
            id="inicio"
            /* pt = alto de la barra fija: el vídeo empieza con canto recto justo
               debajo, en vez de meterse detrás de ella. */
            /* Sin relleno superior: la barra ya no es una franja pegada sino
               una isla flotante, así que el vídeo tiene que empezar en el borde
               mismo de la ventana y verse POR ENCIMA y por los lados de ella.
               `.rp-hero` deja la portada pegada arriba (solo si la ventana es lo
               bastante alta — ver landing.css) y `z-0` la manda al fondo, para
               que el resto de la página pase por encima. `min-h-svh` y no
               `h-svh`: con altura fija, en un teléfono en horizontal el titular
               se salía por arriba y `overflow-hidden` se lo comía.
               `origin-top` para que el encogimiento tire hacia arriba y no
               hacia el centro de la pantalla. */
            data-fondo="navy"
            className="rp-hero z-0 flex min-h-svh origin-top flex-col justify-end overflow-hidden bg-navy-850"
        >
            {/* Montado más alto que el marco para que el paralaje nunca descubra
                un borde vacío. */}
            <video
                ref={videoRef}
                data-hero-video
                /* Incondicional. El efecto de arriba llama además a
                   `play()` a mano, porque el atributo por sí solo depende de
                   que Chrome vea el vídeo silenciado en ese instante. */
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                /* El póster se pinta en cuanto llega el HTML, así que la
                   portada nunca está en negro esperando al vídeo. */
                poster={HERO_POSTER}
                aria-hidden="true"
                tabIndex={-1}
                className="pointer-events-none absolute inset-x-0 -top-[6%] h-[112%] w-full object-cover object-[58%_center]"
            >
                {/* WebM primero: el navegador se queda con la primera fuente que
                    sepa reproducir, y VP9 pesa menos a igual calidad. El MP4 es
                    para Safari, que aún no acepta VP9 en <video>. */}
                <source src={fuente.webm} type="video/webm" />
                <source src={fuente.mp4} type="video/mp4" />
            </video>

            {/* Un solo degradado, anclado abajo. Opaco donde va el texto y casi
                transparente arriba: es lo que permite que la fotografía se vea. */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-navy-850 via-navy-950/55 to-navy-950/10" />
            {/* Sombra que se va cerrando mientras la portada queda debajo del
                siguiente fold. Sin ella, «hundirse» se lee como «encogerse». */}
            <div
                data-hero-sombra
                className="pointer-events-none absolute inset-0 bg-navy-850 opacity-0"
            />

            <div
                data-hero-copia
                className="relative px-5 pb-[clamp(3.5rem,8vw,6rem)] pt-32 sm:px-8"
            >
                <div className="mx-auto max-w-[1240px]">
                    <h1 className="rp-h1 text-white">
                        <Frase marca="titular" className="block">{t('lp.hero.titular1')}</Frase>
                        <Frase marca="titular" className="block">{t('lp.hero.titular2')}</Frase>
                    </h1>

                    <p
                        data-entra
                        className="mt-7 max-w-[38ch] text-[clamp(1.125rem,1.7vw,1.4rem)] leading-snug text-white/85"
                    >
                        {t('lp.hero.sub')}
                    </p>

                    <button
                        ref={boton}
                        data-entra
                        type="button"
                        onClick={onPrimary}
                        className="rp-btn rp-btn--primary mt-10 w-full sm:w-auto"
                    >
                        {signedIn ? t('lp.cta.dashboard') : t('lp.cta.gratis')}
                        <ArrowRight className="h-[18px] w-[18px]" aria-hidden="true" />
                    </button>
                </div>
            </div>
        </section>
    );
};

export default Hero;
